import { useState, useRef, useEffect } from 'react';
import { Search, X, Loader2, Database } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useCptSearch } from '@/hooks/useCptSearch';
import type { CptSearchContext, CptSearchEntry } from '@/lib/cptSearch/catalog';

interface CptDescriptionSearchProps {
  onSelect: (result: { cptCode: string; description: string; category: string }) => void;
  context?: CptSearchContext;
}

function sourceLabel(result: CptSearchEntry): string {
  if (result.preAuthorized) return 'Authorized';
  if (result.plausible) return 'Plausible';
  if (result.source === 'cpt_catalog') return 'Catalog';
  if (result.source === 'procedure') return 'Procedure';
  if (result.source === 'proof_map') return 'Proof Map';
  return result.source.replace('_', ' ');
}

export function CptDescriptionSearch({ onSelect, context }: CptDescriptionSearchProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { results, isSearching } = useCptSearch(query, {
    ...context,
    limit: 20,
  });

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setIsOpen(query.trim().length >= 2 && results.length > 0);
  }, [query, results.length]);

  const handleInputChange = (value: string) => {
    setQuery(value);
  };

  const handleSelect = (result: CptSearchEntry) => {
    onSelect({
      cptCode: result.code,
      description: result.description,
      category: result.category || result.source,
    });
    setQuery('');
    setIsOpen(false);
  };

  const clearSearch = () => {
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder="Search by procedure name or CPT code…"
          className="pl-8 pr-8 h-9 text-sm bg-background"
        />
        {isSearching && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground animate-spin" />
        )}
        {!isSearching && query && (
          <button
            onClick={clearSearch}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
          {results.map((r, i) => (
            <button
              key={`${r.code}-${i}`}
              onClick={() => handleSelect(r)}
              className={cn(
                'w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors',
                'hover:bg-accent/50 focus:bg-accent/50 focus:outline-none',
                i > 0 && 'border-t border-border/40'
              )}
            >
              <Database className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 font-mono bg-primary/5 text-primary border-primary/20 shrink-0"
                  >
                    {r.code}
                  </Badge>
                  <span className="text-xs text-muted-foreground capitalize">{sourceLabel(r)}</span>
                </div>
                <p className="text-sm text-foreground mt-0.5 leading-snug line-clamp-2">
                  {r.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
